import { LightingModeType, type Maybe, TransactionState } from "types";
import { AppState } from "types/mainTypes";

export type ConvergenceInput = {
	appState: AppState;
	hasTransaction: boolean;
	transactionState: Maybe<TransactionState>;
	openDetailDishTypeId: string | null;
	allergens: string[];
	filteredDishTypeIds: readonly string[];
};

// The FE is the sole authority on lighting: a single desired target computed purely from FE state
// (presence + UI + transaction). The driver fires this on each change-of-intent and never reconciles
// against the BE's echo (which is what produced the STAND_BY↔ACTIVE command war). There is no
// "be-authoritative" outcome any more — the one place the BE still matters (its single post-order
// StandBy) is handled as a bounded, one-shot re-assert inside the driver, not here.
export type DesiredFridgeLighting = {
	mode: LightingModeType;
	spotlightDishTypeIds: readonly string[];
};

// Precedence (first match wins): (IDLE && !hasTransaction) → allergen-filter → detail-open → default Active.
// Note FINISHED is NOT special: a finished/just-finished transaction falls through to default Active
// (browse) — the driver brightens the fridge for the next customer; step 5 (idle) dims it later.
export const computeDesiredLighting = (input: ConvergenceInput): DesiredFridgeLighting => {
	const { transactionState, appState, hasTransaction, allergens, filteredDishTypeIds, openDetailDishTypeId } = input;

	// Dwell: idle with no transaction → dim. An active transaction forbids StandBy (the customer is
	// mid-purchase), so this only triggers during idle browse.
	if (appState === AppState.IDLE && !hasTransaction) {
		return { mode: LightingModeType.StandBy, spotlightDishTypeIds: [] };
	}

	// Allergen filter: spotlight the matching dishes (or the open detail if it is one of them).
	// Suppressed during StockCheck, where the order flow owns the screen.
	if (allergens.length > 0 && transactionState !== TransactionState.StockCheck) {
		if (openDetailDishTypeId !== null && filteredDishTypeIds.includes(openDetailDishTypeId)) {
			return { mode: LightingModeType.Active, spotlightDishTypeIds: [openDetailDishTypeId] };
		}

		return { mode: LightingModeType.Active, spotlightDishTypeIds: filteredDishTypeIds };
	}

	// Detail open: spotlight just that dish.
	if (openDetailDishTypeId !== null) {
		return { mode: LightingModeType.Active, spotlightDishTypeIds: [openDetailDishTypeId] };
	}

	// Default Active with no spotlight — active browse (with or without a transaction) and the state a
	// closed detail returns to (so closing a detail clears the spotlight back to all-default).
	return { mode: LightingModeType.Active, spotlightDishTypeIds: [] };
};
